import React from 'react';
import './Legal.css';

const PrivacyPolicy = () => {
  return (
    <div className="legal-container">
      <div className="legal-content">
        <h1>Privacy Policy</h1>
        <p className="legal-date">Last Updated: October 22, 2024</p>

        <section>
          <h2>1. Introduction</h2>
          <p>
            Paragon Wheel Repair ("we," "our," or "us") respects your privacy and is committed to protecting your personal information. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use our application.
          </p>
        </section>

        <section>
          <h2>2. Information We Collect</h2>
          <h3>2.1 Information You Provide</h3>
          <p>We collect information that you voluntarily provide to us, including:</p>
          <ul>
            <li>Account information (username, password, email address)</li>
            <li>Customer information (names, phone numbers, addresses)</li>
            <li>Work order and job order details</li>
            <li>Vehicle and service information</li>
            <li>Payment information (processed through third-party payment processors)</li>
            <li>Notes, descriptions, and other data you enter into the Application</li>
          </ul>

          <h3>2.2 Automatically Collected Information</h3>
          <p>When you use our Application, we may automatically collect:</p>
          <ul>
            <li>Device information (IP address, browser type, operating system)</li>
            <li>Usage data (features used, actions taken, time spent)</li>
            <li>Log data (access times, error logs)</li>
          </ul>
        </section>

        <section>
          <h2>3. How We Use Your Information</h2>
          <p>We use the information we collect to:</p>
          <ul>
            <li>Provide, maintain, and improve the Application</li>
            <li>Process and manage work orders, jobs, and invoices</li>
            <li>Send notifications about work orders and jobs</li>
            <li>Communicate with you about the Application</li>
            <li>Integrate with third-party services (such as QuickBooks)</li>
            <li>Analyze usage patterns and improve user experience</li>
            <li>Detect, prevent, and address technical issues and security threats</li>
            <li>Comply with legal obligations</li>
          </ul>
        </section>

        <section>
          <h2>4. Information Sharing and Disclosure</h2>
          <p>We may share your information in the following circumstances:</p>
          <ul>
            <li><strong>With Your Consent:</strong> We may share information with your explicit consent</li>
            <li><strong>Service Providers:</strong> We may share information with third-party service providers who perform services on our behalf (e.g., hosting, analytics, payment processing)</li>
            <li><strong>Third-Party Integrations:</strong> When you connect third-party services (like QuickBooks), we share necessary information to enable the integration</li>
            <li><strong>Legal Requirements:</strong> We may disclose information if required by law or in response to valid legal requests</li>
            <li><strong>Business Transfers:</strong> If we are involved in a merger, acquisition, or sale of assets, your information may be transferred</li>
          </ul>
          <p>We do not sell your personal information to third parties.</p>
          <p><strong>Disclaimer:</strong> This Application is not affiliated with, endorsed, or sponsored by Intuit Inc. or QuickBooks.</p>
        </section>

        <section>
          <h2>5. Data Security</h2>
          <p>
            We implement appropriate technical and organizational measures to protect your information against unauthorized access, alteration, disclosure, or destruction. However, no method of transmission over the Internet or electronic storage is 100% secure, and we cannot guarantee absolute security.
          </p>
        </section>

        <section>
          <h2>6. Data Retention</h2>
          <p>
            We retain your information for as long as necessary to provide the Application services, comply with legal obligations, resolve disputes, and enforce our agreements. When information is no longer needed, we will securely delete or anonymize it.
          </p>
        </section>

        <section>
          <h2>7. Your Rights and Choices</h2>
          <p>Depending on your location, you may have certain rights regarding your personal information:</p>
          <ul>
            <li><strong>Access:</strong> You can request access to the personal information we hold about you</li>
            <li><strong>Correction:</strong> You can request correction of inaccurate or incomplete information</li>
            <li><strong>Deletion:</strong> You can request deletion of your personal information</li>
            <li><strong>Data Portability:</strong> You can request a copy of your data in a portable format</li>
            <li><strong>Opt-Out:</strong> You can opt out of certain notifications and communications</li>
          </ul>
          <p>To exercise these rights, please contact us using the information provided below.</p>
        </section>

        <section>
          <h2>8. Third-Party Services</h2>
          <p>
            The Application may contain links to or integrate with third-party websites and services. We are not responsible for the privacy practices of these third parties. We encourage you to review the privacy policies of any third-party services you access.
          </p>
        </section>

        <section>
          <h2>9. Children's Privacy</h2>
          <p>
            The Application is not intended for use by children under the age of 13. We do not knowingly collect personal information from children under 13. If we become aware that we have collected information from a child under 13, we will take steps to delete such information.
          </p>
        </section>

        <section>
          <h2>10. Cookies and Tracking Technologies</h2>
          <p>
            We use cookies and similar tracking technologies to track activity on our Application and store certain information. You can configure your browser to refuse cookies or alert you when cookies are being sent. However, some features of the Application may not function properly without cookies.
          </p>
        </section>

        <section>
          <h2>11. Push Notifications</h2>
          <p>
            With your consent, we may send you push notifications about work orders, jobs, and other relevant updates. You can disable push notifications at any time through your device settings or within the Application.
          </p>
        </section>

        <section>
          <h2>12. California Privacy Rights</h2>
          <p>
            If you are a California resident, you have additional rights under the California Consumer Privacy Act (CCPA), including the right to request information about the categories of personal information we collect and the right to opt out of the sale of personal information (though we do not sell personal information).
          </p>
        </section>

        <section>
          <h2>13. International Data Transfers</h2>
          <p>
            Your information may be processed and stored in the United States. If you access the Application from outside the United States, you acknowledge that your information may be transferred to and maintained on servers located in the United States.
          </p>
        </section>

        <section>
          <h2>14. Changes to This Privacy Policy</h2>
          <p>
            We may update this Privacy Policy from time to time. We will notify you of any changes by posting the new Privacy Policy on this page and updating the "Last Updated" date. We encourage you to review this Privacy Policy periodically for any changes.
          </p>
        </section>

        <section>
          <h2>15. Contact Us</h2>
          <p>
            If you have any questions about this Privacy Policy or our privacy practices, please contact us at:
          </p>
          <p>
            Paragon Wheel Repair<br/>
            Email: info@paragonwheel.com
          </p>
        </section>
      </div>
    </div>
  );
};

export default PrivacyPolicy;

